import React from "react";
import "./ViewerPreviewModal.css";

const VIEWER_TYPES = {
    "ohif": { label: "OHIF Basic", icon: "fa-eye", color: "#007bff" },
    "ohif-segmentation": { label: "OHIF Segmentation", icon: "fa-cut", color: "#dc3545" },
    "ohif-3d": { label: "OHIF 3D", icon: "fa-cube", color: "#28a745" },
    "ohif-microscopy": { label: "OHIF Microscopy", icon: "fa-microscope", color: "#6f42c1" },
    "kitware-volview": { label: "Kitware VolView", icon: "fa-cube", color: "#fd7e14" },
    "stone-web-viewer": { label: "Stone Viewer", icon: "fa-image", color: "#17a2b8" },
    "dv2": { label: "DV2 Multimodal", icon: "fa-desktop", color: "#20c997" },
    "discovery-viewer-test": { label: "Discovery Test", icon: "fa-flask", color: "#e83e8c" },
    "image-viewer": { label: "Image Viewer", icon: "fa-image", color: "#6c757d" },
    "document-viewer": { label: "Document Viewer", icon: "fa-file-text", color: "#795548" },
    "pme": { label: "PME Viewer", icon: "fa-microscope", color: "#e91e63" },
    "custom": { label: "Custom", icon: "fa-cog", color: "#6c757d" },
};

const LAYOUT_NAMES = {
    "single": "Single View",
    "horizontal-2": "Horizontal Split",
    "vertical-2": "Vertical Split",
    "grid-4": "Quad View",
    "main-sidebar": "Main + Sidebar",
    "triple-horizontal": "Triple Horizontal",
    "main-half-split": "Main + Half Split",
    "top-split-bottom": "Top Split + Bottom",
};

const ViewerPreviewModal = ({ viewer, onClose, onDownloadTemplate }) => {
    if (!viewer) return null;

    const config = viewer.config || {};
    const panels = config.panels || [];
    const layoutName = LAYOUT_NAMES[config.layout] || config.layout || "Custom Layout";
    const viewerTypeInfo = VIEWER_TYPES[viewer.viewer_type] || VIEWER_TYPES["custom"];

    // If no panels configured, show the viewer type as a single panel
    const displayPanels = panels.length > 0 ? panels : [
        { id: 1, x: 0, y: 0, w: 100, h: 100, viewer_type: viewer.viewer_type }
    ];

    return (
        <div className="viewer-preview-overlay" onClick={onClose}>
            <div className="viewer-preview-modal" onClick={(e) => e.stopPropagation()}>
                <div className="viewer-preview-header">
                    <div className="viewer-preview-title">
                        <i
                            className={`fa ${viewer.icon || viewerTypeInfo.icon}`}
                            style={{ color: viewer.color || viewerTypeInfo.color }}
                        ></i>
                        <h3>{viewer.name}</h3>
                    </div>
                    <button className="viewer-preview-close" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                <div className="viewer-preview-content">
                    {/* Viewer Info Section */}
                    <div className="viewer-info-section">
                        <div className="viewer-info-grid">
                            <div className="viewer-info-item">
                                <label>Type</label>
                                <span className="badge" style={{ backgroundColor: viewerTypeInfo.color }}>
                                    <i className={`fa ${viewerTypeInfo.icon} me-1`}></i>
                                    {viewer.viewer_type_display || viewerTypeInfo.label}
                                </span>
                            </div>
                            <div className="viewer-info-item">
                                <label>Layout</label>
                                <span>{layoutName}</span>
                            </div>
                            <div className="viewer-info-item">
                                <label>Data Types</label>
                                <span className="badge bg-info">
                                    {viewer.supported_data_types_display || viewer.supported_data_types}
                                </span>
                            </div>
                            <div className="viewer-info-item">
                                <label>Status</label>
                                <span className={`badge ${viewer.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {viewer.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                        {viewer.description && (
                            <div className="viewer-description">
                                <label>Description</label>
                                <p>{viewer.description}</p>
                            </div>
                        )}
                    </div>

                    {/* Layout Preview Section */}
                    <div className="layout-preview-section">
                        <h4>
                            <i className="fa fa-th-large me-2"></i>
                            Layout Preview
                        </h4>
                        <div className="layout-preview-container">
                            <div className="layout-preview-screen">
                                {displayPanels.map((panel, index) => {
                                    const panelViewer = VIEWER_TYPES[panel.viewer_type] || null;
                                    return (
                                        <div
                                            key={panel.id || index}
                                            className={`layout-panel ${panelViewer ? 'assigned' : 'empty'}`}
                                            style={{
                                                left: `${panel.x}%`,
                                                top: `${panel.y}%`,
                                                width: `${panel.w}%`,
                                                height: `${panel.h}%`,
                                                '--panel-color': panelViewer?.color || '#3a3f47'
                                            }}
                                        >
                                            {panelViewer ? (
                                                <div className="panel-content">
                                                    <i className={`fa ${panelViewer.icon}`}></i>
                                                    <span>{panelViewer.label}</span>
                                                </div>
                                            ) : (
                                                <div className="panel-empty">
                                                    <i className="fa fa-square-o"></i>
                                                    <span>Empty</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Panel Details */}
                    {displayPanels.length > 1 && (
                        <div className="panel-details-section">
                            <h4>
                                <i className="fa fa-list me-2"></i>
                                Panel Configuration
                            </h4>
                            <div className="panel-details-list">
                                {displayPanels.map((panel, index) => {
                                    const panelViewer = VIEWER_TYPES[panel.viewer_type] || null;
                                    return (
                                        <div key={panel.id || index} className="panel-detail-item">
                                            <span className="panel-number">Panel {panel.id || index + 1}</span>
                                            {panelViewer ? (
                                                <span
                                                    className="panel-viewer-badge"
                                                    style={{ backgroundColor: panelViewer.color }}
                                                >
                                                    <i className={`fa ${panelViewer.icon} me-1`}></i>
                                                    {panelViewer.label}
                                                </span>
                                            ) : (
                                                <span className="panel-viewer-badge empty">Not configured</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Studies Assigned */}
                    {viewer.studies_detail && viewer.studies_detail.length > 0 && (
                        <div className="assigned-studies-section">
                            <h4>
                                <i className="fa fa-flask me-2"></i>
                                Assigned to Studies ({viewer.studies_detail.length})
                            </h4>
                            <div className="assigned-studies-list">
                                {viewer.studies_detail.map(study => (
                                    <div key={study.id} className="assigned-study-item">
                                        <span className="study-name">{study.name}</span>
                                        {study.project_name && (
                                            <span className="study-project">{study.project_name}</span>
                                        )}
                                        <span className={`badge bg-${study.status === 'active' ? 'primary' : 'secondary'}`}>
                                            {study.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="viewer-preview-footer">
                    {onDownloadTemplate && (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => onDownloadTemplate(viewer)}
                            title="Download Viewer Template"
                        >
                            <i className="fa fa-download me-2"></i>
                            Download Template
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewerPreviewModal;
